import { useCallback, useEffect, useState } from "react";
import { loadHistory, loadMarkets, loadOverview } from "./market";
import type {
  CoinId,
  HistoryDays,
  HistoryResponse,
  MarketsResponse,
  OverviewResponse,
} from "./types";

function redirectOn401(err: unknown): boolean {
  if ((err as { status?: number }).status === 401) {
    window.location.assign("/login");
    return true;
  }
  return false;
}

export function useCryptoOverview() {
  const [data, setData] = useState<OverviewResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setRefreshing(true);
    setError(null);
    try {
      setData(await loadOverview());
    } catch (err) {
      if (redirectOn401(err)) return;
      setError("Die Krypto-Kurse sind momentan nicht erreichbar. Bitte versuchen Sie es erneut.");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  return { data, loading, refreshing, error, refresh };
}

export function useCryptoMarkets() {
  const [data, setData] = useState<MarketsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(null);
    loadMarkets()
      .then((result) => {
        if (active) setData(result);
      })
      .catch((err: { status?: number }) => {
        if (!active || redirectOn401(err)) return;
        setError("Markttabellen momentan nicht verfügbar.");
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, []);

  return { data, loading, error };
}

export function useCryptoHistory(id: CoinId, days: HistoryDays) {
  const [data, setData] = useState<HistoryResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(null);
    loadHistory(id, days)
      .then((result) => {
        if (active) setData(result);
      })
      .catch((err: { status?: number }) => {
        if (!active || redirectOn401(err)) return;
        setError("Kursverlauf momentan nicht verfügbar.");
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [id, days]);

  return { data, loading, error };
}
